import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  FileText, MessageSquare, Shield, Zap, Check, ChevronRight, 
  Smartphone, FileCode, Clock, Globe, Download, Bot, 
  RefreshCw, CreditCard, Users, Building2, FileKey,
  ArrowRight, Sparkles, Calendar, Bell, Brain,
  Mic, Gift, Database, TrendingUp, Code, Truck, ClipboardList, 
  Wrench, Target, Award
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1657727534676-cac1bb160d64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxhY2NvdW50YW50JTIwbW9kZXJuJTIwZGVza3xlbnwwfHx8fDE3NzQzMTExODd8MA&ixlib=rb-4.1.0&q=85";
const WHATSAPP_IMAGE = "https://images.pexels.com/photos/46924/pexels-photo-46924.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LandingPage = () => {
  const mainFeatures = [
    {
      icon: <FileCode className="w-6 h-6" />,
      title: "Timbrado CFDI 4.0",
      description: "Emite facturas, notas de crédito y complementos de pago con validez fiscal ante el SAT"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Factura por WhatsApp",
      description: "Dicta tu factura por mensaje y recibe el CFDI timbrado al instante. ¡Sin abrir la computadora!"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "100% Válido ante el SAT",
      description: "Conexión directa con PAC autorizado (Facturama). Tus CFDI cumplen con todas las disposiciones"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Timbrado Instantáneo",
      description: "Genera tu XML y PDF en segundos con folio fiscal y sello digital del SAT"
    }
  ];

  const newFeatures = [
    {
      icon: <Bot className="w-8 h-8 text-green-500" />,
      title: "Chatbot Inteligente con IA",
      description: "Nuestro bot de WhatsApp te conoce por tu nombre, recuerda tus preferencias y te ayuda a facturar conversando.",
      badge: "EXCLUSIVO"
    },
    {
      icon: <Download className="w-8 h-8 text-blue-500" />,
      title: "Descarga Automática CSF y 32-D",
      description: "El bot descarga tu Constancia de Situación Fiscal y Opinión de Cumplimiento directo del SAT sin CAPTCHA.",
      badge: "EXCLUSIVO"
    },
    {
      icon: <Mic className="w-8 h-8 text-red-500" />,
      title: "Facturación por Voz",
      description: "Dicta tu factura por mensaje de voz: 'Hazme una factura a Grupo Bimbo por $5,000 de consultoría'. ¡Así de fácil!",
      badge: "EXCLUSIVO"
    },
    {
      icon: <Wrench className="w-8 h-8 text-cyan-500" />,
      title: "Sistema AI Auto-Repair",
      description: "Nuestro sistema detecta y soluciona problemas automáticamente. Recibe notificaciones solo cuando se necesita tu aprobación.",
      badge: "EXCLUSIVO"
    },
    {
      icon: <ClipboardList className="w-8 h-8 text-amber-500" />,
      title: "Cotizaciones (Quotes)",
      description: "Crea cotizaciones profesionales y conviértelas a factura CFDI con un solo clic cuando tu cliente acepte.",
      badge: "NUEVO"
    },
    {
      icon: <Truck className="w-8 h-8 text-indigo-500" />,
      title: "Carta Porte 3.1",
      description: "Complemento obligatorio para transporte de mercancías. Cumple con la normativa SAT desde enero 2024.",
      badge: "NUEVO"
    },
    {
      icon: <Gift className="w-8 h-8 text-purple-500" />,
      title: "Programa de Referidos",
      description: "Invita amigos y gana 30 días gratis por cada uno. Ellos obtienen 20% de descuento. ¡Todos ganan!",
      badge: "NUEVO"
    },
    {
      icon: <Database className="w-8 h-8 text-teal-500" />,
      title: "Exporta a Contpaqi/Aspel",
      description: "Genera archivos compatibles con CONTPAQi y Aspel. Integración perfecta con tu contador.",
      badge: "NUEVO"
    },
    {
      icon: <Users className="w-8 h-8 text-pink-500" />,
      title: "Multi-RFC y Equipos",
      description: "Administra múltiples RFCs desde una sola cuenta. Invita a tu equipo con roles personalizados.",
      badge: "PRO"
    }
  ];

  const cfdiTypes = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Facturas (Ingreso)",
      description: "CFDI de ingreso para ventas de productos y servicios"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Notas de Crédito (Egreso)",
      description: "CFDI de egreso para devoluciones, descuentos y bonificaciones"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Complementos de Pago (REP)",
      description: "Recibos Electrónicos de Pago para facturas con método PPD"
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "$299",
      period: "/mes",
      features: [
        "50 CFDI al mes",
        "Bot WhatsApp básico",
        "Descarga automática CSF y Opinión de Cumplimiento",
        "Integración contable",
        "1 RFC",
        "Soporte por email"
      ],
      popular: false
    },
    {
      name: "Profesional",
      price: "$599",
      period: "/mes",
      features: [
        "200 CFDI al mes",
        "Bot WhatsApp con IA",
        "Descarga automática CSF y Opinión de Cumplimiento",
        "Recordatorios automáticos",
        "PDF personalizable",
        "Integración contable",
        "1 RFC",
        "Soporte prioritario"
      ],
      popular: true
    },
    {
      name: "Empresarial",
      price: "$1,199",
      period: "/mes",
      features: [
        "CFDI ilimitados",
        "Todo de Profesional",
        "Hasta 5 RFCs",
        "Multi-usuario",
        "API pública para tus clientes",
        "Facturación white-label",
        "Soporte 24/7"
      ],
      popular: false
    }
  ];

  const whatsappCommands = [
    { command: '"factura"', action: "Crear una nueva factura" },
    { command: '"constancia"', action: "Descargar CSF del SAT" },
    { command: '"opinión"', action: "Descargar 32-D del SAT" },
    { command: '"reporte"', action: "Ver resumen de facturación" },
    { command: '"cliente"', action: "Registrar nuevo cliente" },
    { command: '"suscripción"', action: "Ver/pagar planes" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="CIA Factura℗" className="h-10 w-auto" />
              <span className="font-bold text-xl text-slate-900">CIA Factura℗</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Características</a>
              <a href="#whatsapp" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Claudi@</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Precios</a>
              <a href="#new" className="text-slate-600 hover:text-slate-900 text-sm font-medium flex items-center gap-1">
                Novedades
                <span className="bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">NEW</span>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="login-nav-btn">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white" data-testid="register-nav-btn">
                  Comenzar Gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Nuevo: Bot de WhatsApp con Inteligencia Artificial
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-slate-900 mb-6">
                Timbra CFDI desde{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
                  WhatsApp
                </span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                La plataforma de facturación electrónica más inteligente de México. 
                Dicta tus facturas, descarga tu CSF automáticamente y recibe recordatorios 
                personalizados — todo desde el chat de WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white px-8" data-testid="hero-cta-btn">
                    Comenzar Gratis
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/523321015600?text=Hola%20quiero%20facturar" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-green-500 text-green-600 hover:bg-green-50 px-8" data-testid="whatsapp-btn">
                    <MessageSquare className="mr-2 w-5 h-5" />
                    Chatear por WhatsApp
                  </Button>
                </a>
              </div>
              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  7 días gratis
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Sin tarjeta de crédito
                </span>
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  PAC autorizado SAT
                </span>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-3xl"></div>
                {/* WhatsApp Chat Preview */}
                <div className="relative bg-[#ECE5DD] rounded-2xl shadow-2xl overflow-hidden max-w-md mx-auto">
                  <div className="bg-[#075E54] text-white p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-lg">👩‍💼</span>
                    </div>
                    <div>
                      <p className="font-semibold">Claudi@ 💼</p>
                      <p className="text-xs text-white/70">Presidencia de Facturación • en línea</p>
                    </div>
                  </div>
                  <div className="p-4 space-y-3 min-h-[320px]">
                    {/* Bot message */}
                    <div className="flex justify-start">
                      <div className="bg-white rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                        <p className="text-sm text-slate-700">¡Hola Tony! Soy Claudi@ de la Presidencia de Facturación 😊 ¿En qué te ayudo?</p>
                        <p className="text-[10px] text-slate-400 text-right mt-1">10:30</p>
                      </div>
                    </div>
                    {/* User message */}
                    <div className="flex justify-end">
                      <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none p-3 max-w-[85%] shadow-sm">
                        <p className="text-sm text-slate-700">necesito mi constancia</p>
                        <p className="text-[10px] text-slate-400 text-right mt-1">10:31</p>
                      </div>
                    </div>
                    {/* Bot message with action */}
                    <div className="flex justify-start">
                      <div className="bg-white rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                        <p className="text-sm text-slate-700">¡Sale! Dame unos segundos, voy al portal del SAT... ⏳</p>
                        <p className="text-[10px] text-slate-400 text-right mt-1">10:31</p>
                      </div>
                    </div>
                    {/* Bot message with file */}
                    <div className="flex justify-start">
                      <div className="bg-white rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                        <div className="flex items-center gap-2 bg-slate-100 rounded p-2 mb-2">
                          <FileText className="w-8 h-8 text-red-500" />
                          <div>
                            <p className="text-xs font-medium">CSF_XAXX010101000.pdf</p>
                            <p className="text-[10px] text-slate-400">PDF • 245 KB</p>
                          </div>
                        </div>
                        <p className="text-sm text-slate-700">¡Listo! Aquí está tu Constancia actualizada 📄✨</p>
                        <p className="text-[10px] text-slate-400 text-right mt-1">10:32</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Different Section */}
      <section className="py-16 bg-white border-b">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4" />
              Lo que nos hace diferentes
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              4 razones por las que somos únicos en México
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ningún otro sistema de facturación ofrece estas capacidades
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-green-200 bg-green-50/50 hover:border-green-400 transition-all">
              <CardContent className="p-6 text-center flex flex-col h-full">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Asistente Virtual WhatsApp</h3>
                <p className="text-sm text-slate-600 flex-grow">
                  El único sistema con un asistente virtual de WhatsApp para todas tus solicitudes de facturación.
                </p>
                <span className="inline-block mt-4 text-xs font-bold text-green-600 bg-green-100 px-3 py-1 rounded-full">
                  Solo CIA Factura
                </span>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-blue-200 bg-blue-50/50 hover:border-blue-400 transition-all">
              <CardContent className="p-6 text-center flex flex-col h-full">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Download className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">CSF y 32-D Instantáneos</h3>
                <p className="text-sm text-slate-600 flex-grow">
                  El único que descarga tu Constancia de Situación Fiscal y Opinión de Cumplimiento en segundos, sin hacer nada más.
                </p>
                <span className="inline-block mt-4 text-xs font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  Solo CIA Factura
                </span>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-red-200 bg-red-50/50 hover:border-red-400 transition-all">
              <CardContent className="p-6 text-center flex flex-col h-full">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mic className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Facturación por Voz</h3>
                <p className="text-sm text-slate-600 flex-grow">
                  Envía un audio a WhatsApp y recibe tu CFDI timbrado. Así de fácil.
                </p>
                <span className="inline-block mt-4 text-xs font-bold text-red-600 bg-red-100 px-3 py-1 rounded-full">
                  Solo CIA Factura
                </span>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-purple-200 bg-purple-50/50 hover:border-purple-400 transition-all">
              <CardContent className="p-6 text-center flex flex-col h-full">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Agenda y Automatiza</h3>
                <p className="text-sm text-slate-600 flex-grow">
                  Programa el envío automático de constancias y CFDIs. Tú decides cuándo y nosotros lo hacemos por ti.
                </p>
                <span className="inline-block mt-4 text-xs font-bold text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
                  Solo CIA Factura
                </span>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* New Features Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800" id="new">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Funcionalidades Completas
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white mb-4">
              Todo lo que ofrece CIA Factura
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Desde cotizaciones hasta Carta Porte 3.1 — tenemos todo lo que necesita tu negocio
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {newFeatures.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-all group"
                data-testid={`new-feature-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-xl bg-slate-700 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    {feature.badge && (
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                        feature.badge === "NUEVO" ? "bg-green-500/20 text-green-400" : 
                        "bg-purple-500/20 text-purple-400"
                      }`}>
                        {feature.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Bot Section */}
      <section className="py-20 bg-white" id="whatsapp">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MessageSquare className="w-4 h-4" />
                Claudi@ por WhatsApp
              </span>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-6">
                Factura sin abrir la computadora
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Claudi@, de la Presidencia de Facturación, te permite hacer todo desde WhatsApp: crear facturas, 
                registrar clientes, descargar documentos del SAT e incluso pagar tu suscripción.
              </p>
              
              <div className="bg-slate-50 rounded-xl p-6 mb-8">
                <h4 className="font-semibold text-slate-900 mb-4">Comandos más usados:</h4>
                <div className="grid grid-cols-2 gap-3">
                  {whatsappCommands.map((cmd, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <code className="bg-white px-2 py-1 rounded text-green-600 font-mono">
                        {cmd.command}
                      </code>
                      <span className="text-slate-600">{cmd.action}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/523321015600?text=Hola%20quiero%20facturar" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-green-500 hover:bg-green-600 text-white">
                    <MessageSquare className="mr-2 w-5 h-5" />
                    Chatear con Claudi@
                  </Button>
                </a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/manual`} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline">
                    Ver Manual Completo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-3xl"></div>
              <img 
                src={WHATSAPP_IMAGE} 
                alt="WhatsApp Business" 
                className="relative rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">👩‍💼</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Claudi@ con IA</p>
                  <p className="text-sm text-slate-500">Presidencia de Facturación</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50" id="features">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-4 block">
              Características
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Todo lo que necesitas para timbrar
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Sistema completo de facturación electrónica diseñado para empresarios y contadores mexicanos
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainFeatures.map((feature, index) => (
              <Card 
                key={index} 
                className="hover:shadow-lg transition-all group border-slate-200"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CFDI Types Section */}
      <section className="py-20 bg-white" id="cfdi">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-4 block">
              Tipos de Comprobante
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Todos los CFDI que necesitas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Emite cualquier tipo de comprobante fiscal digital con validez total ante el SAT
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cfdiTypes.map((type, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all border-slate-200">
                <CardContent className="pt-8 pb-8">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mx-auto mb-6">
                    {type.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{type.title}</h3>
                  <p className="text-slate-600">{type.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 relative overflow-hidden" id="referidos">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-pink-300 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
                <Gift className="w-4 h-4" />
                Programa de Referidos
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Gana <span className="text-yellow-300">30 días gratis</span> por cada amigo
              </h2>
              <p className="text-lg text-purple-100 mb-8">
                Invita a tus colegas, contadores o socios a CIA Factura℗. Cuando paguen su primera suscripción, 
                tú recibes un mes de servicio gratis y ellos obtienen 20% de descuento.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Comparte tu código único</h4>
                    <p className="text-purple-200 text-sm">Lo encuentras en tu dashboard</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Tu amigo se registra con descuento</h4>
                    <p className="text-purple-200 text-sm">20% menos en su primer pago</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                    <Gift className="w-6 h-6 text-purple-900" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">¡Ganas 30 días gratis!</h4>
                    <p className="text-purple-200 text-sm">Sin límite de referidos</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register">
                <Button size="lg" className="bg-white text-purple-700 hover:bg-purple-50 font-semibold">
                  Crear mi cuenta y obtener código
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-400 rounded-full mb-4">
                    <Gift className="w-10 h-10 text-purple-900" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">¡Es así de fácil!</h3>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4 mb-6">
                  <p className="text-purple-100 text-sm mb-2">Tu código de referido:</p>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <span className="text-2xl font-bold text-purple-700 tracking-wider">TUCOD1234</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-white">∞</p>
                    <p className="text-purple-200 text-sm">Sin límite</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-yellow-300">30</p>
                    <p className="text-purple-200 text-sm">Días/referido</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-white">20%</p>
                    <p className="text-purple-200 text-sm">Descuento</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50/30" id="pricing">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-4 block">
              Precios
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Planes para cada necesidad
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Todos los planes incluyen 7 días de prueba gratis. Cancela cuando quieras.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${plan.popular ? 'border-2 border-blue-500 shadow-xl scale-105' : 'border-slate-200'}`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                    Más Popular
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-black text-slate-900">{plan.price}</span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3 text-slate-600">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button 
                      className={`w-full ${plan.popular 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white' 
                        : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      Comenzar Gratis
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <p className="text-center text-slate-500 mt-8">
            <CreditCard className="w-4 h-4 inline mr-1" />
            Pago seguro con Stripe. También puedes pagar desde WhatsApp.
          </p>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 bg-white" id="integraciones">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-teal-600 mb-4 block">
              Integraciones
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Compatible con tu sistema contable
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Exporta tus facturas en formatos listos para importar a los sistemas contables más usados en México
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Card className="text-center border-2 border-teal-100 hover:border-teal-300 transition-colors">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Database className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">CONTPAQi</h3>
                <p className="text-slate-600 text-sm">
                  Exporta pólizas contables listas para importar a CONTPAQi Contabilidad
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center border-2 border-blue-100 hover:border-blue-300 transition-colors">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Database className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Aspel COI/SAE</h3>
                <p className="text-slate-600 text-sm">
                  Formato CSV compatible con Aspel para importación directa
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center border-2 border-purple-100 hover:border-purple-300 transition-colors">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Excel / CSV</h3>
                <p className="text-slate-600 text-sm">
                  Reportes detallados en Excel para análisis personalizado
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-12 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 max-w-3xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  ¿Tu contador usa otro sistema?
                </h3>
                <p className="text-slate-600">
                  Exporta tus XMLs en lote para que tu contador los importe al sistema que prefiera. 
                  Compatible con cualquier software que acepte CFDIs.
                </p>
              </div>
              <Link to="/register">
                <Button className="bg-teal-600 hover:bg-teal-700 whitespace-nowrap">
                  Probar Gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* API for Developers Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900" id="api">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-indigo-500/20 text-indigo-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Code className="w-4 h-4" />
                API para Desarrolladores
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Integra facturación en <span className="text-indigo-400">tu aplicación</span>
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                API REST completa con SDKs oficiales en Python, JavaScript y PHP. 
                Emite CFDIs desde tu sistema, e-commerce o aplicación personalizada.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Integración en 5 minutos</p>
                    <p className="text-sm text-slate-400">SDKs listos para usar</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Documentación completa</p>
                    <p className="text-sm text-slate-400">Ejemplos y guías paso a paso</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Webhooks en tiempo real</p>
                    <p className="text-sm text-slate-400">Notificaciones automáticas</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/developers">
                  <Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 text-white">
                    <Code className="w-5 h-5 mr-2" />
                    Portal de Desarrolladores
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                    Obtener API Keys
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Code Preview */}
            <div className="hidden lg:block">
              <Card className="bg-slate-900 border-slate-700 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="ml-4 text-sm text-slate-400">factura.py</span>
                </div>
                <CardContent className="p-0">
                  <pre className="p-4 text-sm text-slate-300 font-mono overflow-x-auto">
{`from ciafactura import CIAFactura

client = CIAFactura(
    api_key="cia_pk_xxx",
    api_secret="cia_sk_xxx"
)

invoice = client.invoices.create(
    receptor_rfc="XAXX010101000",
    receptor_name="Cliente SA",
    receptor_email="c@email.com",
    items=[{
        "description": "Servicio",
        "unit_price": 5000.00
    }]
)

print(f"UUID: {invoice.cfdi_uuid}")
# UUID: A1B2C3D4-E5F6-G7H8...`}
                  </pre>
                </CardContent>
              </Card>
              
              {/* SDK Badges */}
              <div className="flex justify-center gap-4 mt-6">
                <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-lg">
                  <span className="text-2xl">🐍</span>
                  <span className="text-white text-sm">Python</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-lg">
                  <span className="text-2xl">🟨</span>
                  <span className="text-white text-sm">JavaScript</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-lg">
                  <span className="text-2xl">🐘</span>
                  <span className="text-white text-sm">PHP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            ¿Listo para simplificar tu facturación?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Únete a miles de empresarios mexicanos que ya timbran sus CFDI desde WhatsApp
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 px-8">
                Crear Cuenta Gratis
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="https://wa.me/523321015600?text=Hola%20quiero%20facturar" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                <MessageSquare className="mr-2 w-5 h-5" />
                Chatear por WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="CIA Factura℗" className="h-8 w-auto" />
                <span className="font-bold text-lg text-white">CIA Factura℗</span>
              </div>
              <p className="text-sm mb-4">
                Plataforma de facturación electrónica CFDI 4.0 con timbrado por WhatsApp.
              </p>
              {/* Social Media Buttons */}
              <div className="flex gap-3">
                <a 
                  href="https://facebook.com/ciafactura" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                  </svg>
                </a>
                <a 
                  href="https://instagram.com/ciafactura" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-red-500 hover:to-purple-500 rounded-full flex items-center justify-center transition-all"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://linkedin.com/company/ciafactura" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a 
                  href="https://twitter.com/ciafactura" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-black rounded-full flex items-center justify-center transition-colors"
                  aria-label="X (Twitter)"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a 
                  href="https://tiktok.com/@ciafactura" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-black rounded-full flex items-center justify-center transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://youtube.com/@ciafactura" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                  aria-label="YouTube"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Producto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Características</a></li>
                <li><a href="#pricing" className="hover:text-white">Precios</a></li>
                <li><a href="#referidos" className="hover:text-white">Programa Referidos</a></li>
                <li><a href="#integraciones" className="hover:text-white">Integraciones</a></li>
                <li><a href="#api" className="hover:text-white">API</a></li>
                <li><Link to="/developers" className="hover:text-white">Desarrolladores</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/contacto" className="hover:text-white">Contacto</Link></li>
                <li><Link to="/privacidad" className="hover:text-white">Privacidad</Link></li>
                <li><Link to="/terminos" className="hover:text-white">Términos</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Soporte</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:soporte@ciafactura.com" className="hover:text-white">soporte@ciafactura.com</a></li>
                <li><a href="https://wa.me/523321015600" className="hover:text-white">WhatsApp: +52 33 2101 5600</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>© 2026 CIA Factura℗. Todos los derechos reservados. PAC autorizado por el SAT.</p>
          </div>
        </div>
      </footer>

      {/* Floating Social Buttons */}
      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-2">
        <a 
          href="https://facebook.com/ciafactura" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="Facebook"
        >
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
          </svg>
        </a>
        <a 
          href="https://instagram.com/ciafactura" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-10 h-10 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 hover:opacity-90 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="Instagram"
        >
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        </a>
        <a 
          href="https://tiktok.com/@ciafactura" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-10 h-10 bg-black hover:bg-gray-800 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="TikTok"
        >
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
          </svg>
        </a>
        <a 
          href="https://wa.me/523321015600?text=Hola%20quiero%20facturar"
          target="_blank" 
          rel="noopener noreferrer"
          className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="WhatsApp"
        >
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </div>
  );
};

export default LandingPage;
